import React, { Component } from "react";
import AbbreviatedMillionNumberFormat from "../../ui-building-blocks/numbers/AbbreviatedMillionNumberFormat";
import {
  BarChart,
  Bar,
  Tooltip,
  ResponsiveContainer,
  XAxis,
  YAxis
} from "recharts";
import { pickBy, sumBy, isNumber } from "lodash";
import {
  Tooltip as ReactBootstrapTooltip,
  OverlayTrigger
} from "react-bootstrap";

import Teams from "api/Teams";
import IconTableHelp from "../../../containers/assets/icon-table-help.svg";
import Styled from "components/operations/style";
import { view } from "@risingstack/react-easy-state";

import DecisionsAlert from "../../alerts/decisions";

function toMillions(amount) {
  const value = amount / 1000000;

  return `  $${value.toFixed(2)}M`;
}

const totalSpendingTooltip = (
  production_costs,
  cash_payments_prior_round_purchases,
  machines_costs,
  capital_exp_sales,
  facility_costs,
  cash_payment_for_distribution_center
) => (
  <ReactBootstrapTooltip id="ops-tooltip">
    Cash spent on production costs:{" "}
    <AbbreviatedMillionNumberFormat value={production_costs} prefix="$" />
    <br />
    Cash payment for distribution center:{" "}
    <AbbreviatedMillionNumberFormat value={cash_payment_for_distribution_center} prefix="$" />
    <br />
    Cash payment for prior round purchases:{" "}
    <AbbreviatedMillionNumberFormat
      value={cash_payments_prior_round_purchases}
      prefix="$"
    />
    <br />
    Cash spent on machine purchases:{" "}
    <AbbreviatedMillionNumberFormat value={machines_costs} prefix="$" />
    <br />
    Cash received from sale of machines:{" "}
    <AbbreviatedMillionNumberFormat value={capital_exp_sales} prefix="$" />
    <br />
    Cash spent on buying facility:{" "}
    <AbbreviatedMillionNumberFormat value={-facility_costs} prefix="$" />
  </ReactBootstrapTooltip>
);

class TotalProductionCost extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isInfoAlertVisible: false,
      infoAlertTitle: '',
      infoAlertText: ''
    };
  }

  showInfoAlert = (infoToShow) => {
    let title, text;
    if (infoToShow === 'Capital Expenditures') {
      title = 'Capital Expenditures';
      text = 'Refers to the funds a company spends to buy or improve things it will use for a long time, like machines, buildings, or land.';
    } else if (infoToShow === 'Product Costs') {
      title = 'Product Cost';
      text = 'Refers to the total expense of making a product, including raw materials, direct labor, and manufacturing overhead like machine depreciation.';
    }

    this.setState({
      isInfoAlertVisible: true,
      infoAlertTitle: title,
      infoAlertText: text
    });
  };

  hideInfoAlert = () => {
    this.setState({
      isInfoAlertVisible: false
    });
  };

  render() {
    const {
      isInfoAlertVisible,
      infoAlertTitle,
      infoAlertText
    } = this.state;

    const {
      capital_expenditures = {},
      total_spending = 0,
      machines_costs = 0,
      production_costs = 0,
      cash_payments_prior_round_purchases = 0,
      capital_exp_sales = 0,
      facility_costs = 0,
      products = [],
      cash_payment_for_distribution_center = 0,
      distributionCenter = 0,
      distributionCentersInfo = [],
      distributionCenterPPE = 0,
    } = this.props;

    const showCapitalExpenditures =
      Object.keys(pickBy(capital_expenditures, i => i !== 0)).length > 0;
    const showProductCosts =
      sumBy(
        products,
        i => i.material_costs + i.labor_costs + i.overhead_costs
      ) > 0;

    const cursorStyle = {
      stroke: "none",
      strokeWidth: 0
    };
    const labelStyle = {
      display: "none"
    };

    const distributionObj = distributionCentersInfo.find(i => Number(i.type) === distributionCenter);
    const distribution = distributionObj ? distributionObj.location : null;

    const capitalExpendituresData = [
      {
        name: "Capital Expenditures",
        ...capital_expenditures,
        ...(distribution ? {
          [`Distribution Center (${distribution})`]: distributionCenterPPE,
        } : {}),
      }
    ];

    const inventoryCostsData = [
      {
        name: "Inventory Costs",
        "Material Costs": sumBy(products, "material_costs"),
        "Labor Costs": sumBy(products, "labor_costs"),
        "Overhead Costs": sumBy(products, "overhead_costs")
      }
    ];
    const productCostsData = pickBy(inventoryCostsData[0], i => isNumber(i));

    return (
      <Styled.FullWidthRow>
        {isInfoAlertVisible && (
          <DecisionsAlert
            type="info"
            title={infoAlertTitle}
            message={infoAlertText}
            buttonText="Done"
            onHide={this.hideInfoAlert}
          />
        )}

        <Styled.Chart.TitleRow>
          <Styled.Chart.Title>Total Spending</Styled.Chart.Title>
          <OverlayTrigger
            placement="bottom"
            overlay={totalSpendingTooltip(
              production_costs,
              cash_payments_prior_round_purchases,
              machines_costs,
              capital_exp_sales,
              facility_costs,
              cash_payment_for_distribution_center
            )}
          >
            <Styled.Chart.TitleAmountTeam>
              <AbbreviatedMillionNumberFormat
                value={total_spending}
                prefix="$"
                floatCount={2}
              />
            </Styled.Chart.TitleAmountTeam>
          </OverlayTrigger>
        </Styled.Chart.TitleRow>
        {showCapitalExpenditures === true && (
          <Styled.Chart.Row>
            Capital Expenditures
            <Styled.HelpIcon src={IconTableHelp} onClick={() => this.showInfoAlert('Capital Expenditures')} />
            {showCapitalExpenditures === true && (
              <Styled.Chart.Wrapper>
                <Styled.Chart.Chart>
                  <ResponsiveContainer width="100%" height={50}>
                    <BarChart
                      data={capitalExpendituresData}
                      layout="vertical"
                      margin={{ top: 0, left: 0, right: 0, bottom: 0 }}
                    >
                      <Tooltip
                        separator=":"
                        cursor={cursorStyle}
                        formatter={toMillions}
                        labelStyle={labelStyle}
                        wrapperStyle={{ zIndex: 1 }}
                      />
                      <XAxis type="number" hide={true} />
                      <YAxis type="category" hide={true} />
                      {Object.keys(capital_expenditures).map(key => (
                        <Bar
                          dataKey={key}
                          key={key}
                          stackId="Capital Expenditures"
                          fill={Teams.capitalExpendituresColor(key)}
                        />
                      ))}
                      {distribution && (
                        <Bar
                          dataKey={`Distribution Center (${distribution})`}
                          stackId="Capital Expenditures"
                          fill={Teams.currentTeamColorLevel1()}
                        />
                      )}
                    </BarChart>
                  </ResponsiveContainer>
                </Styled.Chart.Chart>
                <Styled.Chart.Legend>
                  {Object.keys(capital_expenditures).map(key =>
                    capital_expenditures[key] !== 0 ? (
                      <Styled.Chart.LegendItem key={key}>
                        <Styled.Chart.LegendItemCircle
                          color={Teams.capitalExpendituresColor(key)}
                        />
                        <div>{key}</div>
                      </Styled.Chart.LegendItem>
                    ) : null
                  )}
                  {distribution && (
                    <Styled.Chart.LegendItem>
                      <Styled.Chart.LegendItemCircle
                        color={Teams.currentTeamColorLevel1()}
                      />
                      <div>{`Distribution Center (${distribution})`}</div>
                    </Styled.Chart.LegendItem>
                  )}
                </Styled.Chart.Legend>
              </Styled.Chart.Wrapper>
            )}
          </Styled.Chart.Row>
        )}
        {showProductCosts === true && (
          <Styled.Chart.Row>
            Product Costs
            <Styled.HelpIcon src={IconTableHelp} onClick={() => this.showInfoAlert('Product Costs')} />
            {showProductCosts === true && (
              <Styled.Chart.Wrapper>
                <Styled.Chart.Chart>
                  <ResponsiveContainer width="100%" height={50}>
                    <BarChart
                      data={inventoryCostsData}
                      layout="vertical"
                      margin={{ top: 0, left: 0, right: 0, bottom: 0 }}
                    >
                      <Tooltip
                        separator=":"
                        cursor={cursorStyle}
                        formatter={toMillions}
                        labelStyle={labelStyle}
                        wrapperStyle={{ zIndex: 1 }}
                      />
                      <XAxis type="number" hide={true} />
                      <YAxis type="category" hide={true} />
                      {Object.keys(productCostsData).map(key => (
                        <Bar
                          dataKey={key}
                          key={key}
                          stackId="Product Costs"
                          fill={Teams.productCostsColor(key)}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </Styled.Chart.Chart>
                <Styled.Chart.Legend>
                  {Object.keys(productCostsData).map(key =>
                    productCostsData[key] !== 0 ? (
                      <Styled.Chart.LegendItem key={key}>
                        <Styled.Chart.LegendItemCircle
                          color={Teams.productCostsColor(key)}
                        />
                        <div>{key}</div>
                      </Styled.Chart.LegendItem>
                    ) : null
                  )}
                </Styled.Chart.Legend>
              </Styled.Chart.Wrapper>
            )}
          </Styled.Chart.Row>
        )}
      </Styled.FullWidthRow>
    );
  }
}

export default view(TotalProductionCost);



// WEBPACK FOOTER //
// ./src/components/operations/decisions/total-production-cost.js